/**
 * One-way cleanup of the Cognito / oidc-client-ts auth state the app stored
 * before it moved to Better Auth.
 *
 * Those tokens can't be exchanged for a Better Auth session, so they're only
 * clutter (and a Cognito refresh token is a live credential we no longer need
 * to hold). Runs on every boot, after restoreFromIndexedDB(), because the
 * IndexedDB mirror would otherwise copy them back into localStorage. It is
 * idempotent and cheap once nothing is left.
 *
 * Only auth keys are touched. The user's RxDB data and preferences are kept,
 * so unsynced local changes survive until they sign in again.
 */

import { removeFromIndexedDBWhere } from './authTokenBackup'

/** oidc-client-ts: `oidc.user:<authority>:<client>` plus its `oidc.<state>` entries. */
const LEGACY_PREFIXES = ['oidc.']
/** tokenStorageAdapter.ts: which Cognito flow produced the session. */
const LEGACY_KEYS = ['moly_auth_flow']

export const isLegacyAuthKey = (key: string): boolean =>
  LEGACY_KEYS.includes(key) || LEGACY_PREFIXES.some((prefix) => key.startsWith(prefix))

const sweep = (storage: Storage): number => {
  const doomed: Array<string> = []
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i)
    if (key && isLegacyAuthKey(key)) doomed.push(key)
  }
  for (const key of doomed) storage.removeItem(key)
  return doomed.length
}

/**
 * Remove legacy auth keys from localStorage, sessionStorage and the IndexedDB
 * backup. Returns how many web-storage keys were removed. Never throws.
 */
export async function cleanupLegacyAuthStorage(): Promise<number> {
  let removed = 0
  for (const getStorage of [() => window.localStorage, () => window.sessionStorage]) {
    try {
      removed += sweep(getStorage())
    } catch {
      // Storage blocked: nothing we can clean.
    }
  }
  await removeFromIndexedDBWhere(isLegacyAuthKey)
  return removed
}
